import React, { FC, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import * as OrderSummary from '../../shared/orders/model';
import { ProductCategoryModel } from '../../shared/orders/product';
import { OrderItemProduct, RenderOrderSummary } from '../containers';
import { OrderBadges } from '../containers/housekeeping/attendee/utils';
import { useTranslation } from '../translations';
import { distinct, useConfig, useConvention } from '../utils';
import { Currency } from './Currency';
import { MaterialIconText } from './MaterialIconText';
import { PermissionBoundary } from './PermissionBoundary';
import { HorizontalStack, VerticalStack } from './Stacks';
import { DateTime, MaterialIcon } from '.';
import './OrderItemElement.scss';

interface StaffOrderOptionsProps {
  readonly order: OrderSummary.Order;
  readonly id: string;
}

const StaffOrderOptions: FC<StaffOrderOptionsProps> = ({ order, id }) => {
  const { ts } = useTranslation();
  return (
    <PermissionBoundary inline requiredPermissions="order:view">
      <Link id={`${id}_details`} to={`/housekeeping/orders/${order.id}`}>
        <Button color="primary" style={{ padding: '0px 5px' }}>
          {ts('order_item_element.show_details')}
        </Button>
      </Link>
    </PermissionBoundary>
  );
};

interface CustomerOrderOptionsProps {
  readonly id: string;
  readonly order: OrderSummary.Order;
  readonly onSelect: (option: OrderItemSelectOption) => void;
  readonly hasRefunds?: boolean;
}

const CustomerOrderOptions: FC<CustomerOrderOptionsProps> = ({
  order,
  id,
  hasRefunds,
  onSelect,
}) => {
  const { ts } = useTranslation();
  const convention = useConvention();

  if (order.status !== 'paid') {
    return null;
  }

  const items: ReactElement[] = [];

  if (hasRefunds) {
    items.push(
      <DropdownItem
        id={`${id}RefundHistory`}
        key="refundHistory"
        onClick={() => {
          onSelect('viewRefundHistory');
        }}
      >
        {ts('order_item_element.view_refund_history')}
      </DropdownItem>,
    );
  }

  if (order.breakdown.paid > 0) {
    items.push(
      <DropdownItem
        className="text-danger"
        id={`${id}Refund`}
        key="refund"
        onClick={() => {
          if (convention.disableRefunds !== null) {
            toast.error(ts('order_item_element.refunds_disabled', convention.disableRefunds));
            return;
          }

          onSelect('requestRefund');
        }}
        style={{
          cursor: convention.disableRefunds === null ? undefined : 'not-allowed',
          textDecoration: convention.disableRefunds === null ? undefined : 'line-through',
        }}
      >
        {ts('common.request_refund')}
      </DropdownItem>,
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <UncontrolledDropdown className="mw-100">
      <DropdownToggle id={`${id}MoreOptions`} tag="a">
        <MaterialIcon className="pointer" name="more_horiz" />
      </DropdownToggle>
      <DropdownMenu end>{items}</DropdownMenu>
    </UncontrolledDropdown>
  );
};

const RefundState: FC<{
  readonly refunds?: OrderSummary.Refund[];
  readonly isCustomer?: boolean;
  readonly id: string;
}> = ({ refunds, isCustomer = false, id }) => {
  const { t, ts } = useTranslation();
  if (refunds?.some(({ status }) => status === 'pending')) {
    return (
      <Alert color="warning" id={`${id}RefundPending`}>
        <MaterialIconText name="hourglass_empty">
          {ts(
            isCustomer
              ? 'order_item_element.you_have_a_pending_refund'
              : 'order_item_element.there_is_a_pending_refund',
          )}
        </MaterialIconText>
      </Alert>
    );
  }

  if (refunds?.some(({ status }) => status === 'denied')) {
    return (
      <Alert color="danger" id={`${id}RefundDenied`}>
        <MaterialIconText name="clear">
          {ts(
            isCustomer
              ? 'order_item_element.this_refund_request_denied'
              : 'order_item_element.your_refund_request_denied',
          )}
        </MaterialIconText>
      </Alert>
    );
  }

  const approvedRefund = (refunds ?? [])
    .sort((a, b) => b.id - a.id)
    .find(({ status }) => status === 'approved');

  if (approvedRefund) {
    const currency = (
      <strong>
        <Currency value={approvedRefund.amount} />
      </strong>
    );

    return (
      <Alert color="success" id={`${id}RefundApproved`}>
        <MaterialIconText name="done">
          {isCustomer
            ? t('order_item_element.your_refund_request_approved', currency)
            : t('order_item_element.this_refund_request_approved', currency)}
        </MaterialIconText>
      </Alert>
    );
  }

  return null;
};

const PaymentFlags: FC<{
  readonly payments?: OrderSummary.Payment[];
  readonly isCustomer?: boolean;
  readonly id: string;
}> = ({ payments, isCustomer = false, id }) => {
  const { ts } = useTranslation();
  if (isCustomer) {
    return null;
  }

  const flags: OrderSummary.FlagReason[] = distinct(
    (payments ?? [])
      .filter(({ status }) => status === 'success')
      .reduce<
        OrderSummary.FlagReason[]
      >((allFlags, { flagReasons: newFlags = [] }) => allFlags.concat(newFlags), []),
  );

  if (flags.length === 0) {
    return null;
  }

  return (
    <Alert color="info" id={`${id}TransactionFlagged`}>
      <MaterialIconText name="flag">
        {ts('order_item_element.this_transaction_has_been_tagged')}
        <br />
        <strong>{flags.join(', ')}</strong>
      </MaterialIconText>
    </Alert>
  );
};

export type OrderItemSelectOption = 'requestRefund' | 'viewRefundHistory';

interface OrderItemElementProps extends CustomerOrderOptionsProps {
  readonly categories: ProductCategoryModel[];
  readonly isCustomer?: boolean;
  readonly onUpdate?: () => void;
}

export const OrderItemElement: FC<OrderItemElementProps> = ({
  categories,
  order,
  id,
  isCustomer = false,
  hasRefunds,
  onSelect,
  onUpdate,
}) => {
  const { ts } = useTranslation();
  const convention = useConvention();
  const config = useConfig();
  const orderConvention = config.conventions.find((t) => t.id === order.conventionId);

  if (!orderConvention) {
    return null;
  }

  return (
    <Col className="resultTable order-item-element mb-10" id={id} xs={12}>
      <Card className="filter-table">
        <CardHeader>
          <div className="float-end">
            {isCustomer ? (
              <CustomerOrderOptions
                hasRefunds={hasRefunds}
                id={id}
                onSelect={onSelect}
                order={order}
              />
            ) : (
              <StaffOrderOptions id={id} order={order} />
            )}
          </div>
          <VerticalStack>
            <HorizontalStack>
              <div className="float-start">
                {ts('common.order')} #{order.id}
                {order.conventionId !== convention.id && <>&nbsp;({orderConvention.shortName})</>}
                &mdash;
                <Currency value={order.breakdown.total} />
              </div>
              <div className="float-start margin-bottom-5" style={{ paddingLeft: '5px' }}>
                <OrderBadges order={order} />
              </div>
            </HorizontalStack>
            {order.paidAt && (
              <div className="float-start">
                {ts('common.order_paid')} &nbsp;
                <DateTime value={order.paidAt} />
              </div>
            )}
          </VerticalStack>
        </CardHeader>
        <PaymentFlags id={id} isCustomer={isCustomer} payments={order.payments} />
        <RefundState id={id} isCustomer={isCustomer} refunds={order.refunds} />
        <CardBody>
          {order.orderItems.map((item) => (
            <OrderItemProduct
              item={item}
              key={item.id}
              onUpdate={onUpdate}
              order={order}
              readOnly
            />
          ))}
          {order.orderItems.length > 0 && <hr />}
          <br />
          <h4>{ts('common.order_summary')}</h4>
          <RenderOrderSummary
            categories={categories}
            isCustomer={isCustomer}
            order={order}
            readOnly={order.status === 'paid'}
          />
        </CardBody>
        {isCustomer && order.orderItems.some((o) => !!o.ticket) && (
          <CardFooter className="d-inline-flex align-items-center order-ticket-notice">
            <div className="flex-fill">
              <MaterialIconText name="confirmation_number" type="primary">
                {ts('order_item_element.you_have_one_or_more')}
              </MaterialIconText>
            </div>
            <Link to={`/order/${order.id}/tickets`}>
              <Button color="primary" outline>
                {ts('order_item_element.retrieve_tickets')}
              </Button>
            </Link>
          </CardFooter>
        )}
      </Card>
    </Col>
  );
};
